import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import MyReturnOnRent from "./my-return-on-rent";
import OptCreditBuilder from "./credit-builder";
import RentCalendar from "./rent-calendar";
import { useConsumerDealsData } from "../../Pages/Deals";

import renderCreditBuilderTracker from "../../Organisms/Modals/Credit-Builder/credit-builder-tracker";

import { useModalContext } from "../../Contexts/ModalContext";

import { useCreditReportDetails, useRentDetails } from "../../CustomHooks/Queries";

import { monthNames } from "../../Utils/constants";

import { ArrowLeftCircle, HourGlassOutlinedThin, Surge } from "../../Assets/Icons";

import "./credit-builder.scss";
import "./credit-builder-responsive.scss";

export default function CreditBuilder() {
  const navigate = useNavigate();

  const modalContext = useModalContext();

  const { isLoading: isLoadingCreditReporting, data: creditReporting } = useCreditReportDetails();
  const { isLoading: isLoadingRentDetails, data: rentDetails } = useRentDetails();
  const { isLoadingConsumerDealsData } = useConsumerDealsData();

  const isCreditReportingOptedIn = useMemo(() => {
    return creditReporting?.data?.data.credit_builder?.opted_in;
  }, [creditReporting?.data?.data.credit_builder?.opted_in]);

  const creditBuilderTrackerList = useMemo(() => {
    return creditReporting?.data?.data.credit_builder_tracker;
  }, [creditReporting?.data?.data.credit_builder_tracker]);

  const isMyReturnOnRent = useMemo(() => {
    return rentDetails?.data?.data?.my_return_on_rent?.is_my_return_on_rent_visible;
  }, [rentDetails]);

  const isRentCalendar = useMemo(() => {
    return rentDetails?.data?.data?.rent_calendar?.is_rent_calendar_visible;
  }, [rentDetails]);

  const { rentMatch } = useMemo(() => {
    const returnOnRent = rentDetails?.data?.data?.my_return_on_rent || {};
    const rentMatch = returnOnRent?.rent_match_amount || 0;
    return { rentMatch };
  }, [rentDetails]);

  const isLoading = isLoadingCreditReporting || isLoadingConsumerDealsData || isLoadingRentDetails;

  const CreditBuilderEmptyStateIcon = isCreditReportingOptedIn ? HourGlassOutlinedThin : Surge;

  return (
    <section className="card-section-wrapper">
      <div className="max-width-container">
        <div className="card-section">
          <div className="section-header">
            <button className="go-back-button" onClick={() => navigate(-1)}>
              <ArrowLeftCircle />
            </button>
            <h3 className="section-title">Credit Builder</h3>
            <div className="dummy"></div>
          </div>
          {isLoading ? (
            <div className="loading-content">
              <CircularProgress />
            </div>
          ) : (
            <div className="credit-builder">
              {isMyReturnOnRent && <MyReturnOnRent rentMatch={rentMatch} />}
              <OptCreditBuilder />
              <div className="credit-builder-tracker">
                <div className="section-header">
                  <h3 className="section-title">Credit Builder Tracker</h3>
                  {creditBuilderTrackerList.length > 10 && (
                    <button className="see-all-button" onClick={renderCreditBuilderTracker.bind(this, modalContext)}>
                      View All
                    </button>
                  )}
                </div>
                {creditBuilderTrackerList.length > 0 ? (
                  <>
                    {creditBuilderTrackerList.slice(0, 7).map((payment, idx) => {
                      const isReported = payment?.is_reported;
                      return (
                        <div key={idx} className="credit-builder-item">
                          <h5 className="text-lg-medium color--mist mb-3">
                            {`${monthNames[parseInt(payment.month) - 1] || ""} ${payment.year}`}
                          </h5>
                          <p className="text-base-regular color--mist mb-0">
                            {(isReported ?? false) ? "✅" : "⏸️"} {payment?.description}
                          </p>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="no-records mt-6">
                    <CreditBuilderEmptyStateIcon className="icon" />
                    {isCreditReportingOptedIn
                      ? "Your first Credit Builder report will appear here soon."
                      : "Opt In to Credit Builder to start reporting your rent payments."}
                  </div>
                )}
              </div>
              {isRentCalendar && <RentCalendar rentMatch={rentMatch} />}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
